import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { of } from 'rxjs';
import { getReadableNameForContract } from '../../contracts/api/getReadableNameForContract.js';
import { Person } from '../../users/entities/person.js';
import { HistoryAPI } from '../api/HistoryAPI.js';
import { HistoryTimeline } from './HistoryTimeline.js';

export const SgUserTimeline = stateful((props: { personID: Maybe<string> }) => {
  const { value: referenceIDs, loading: referenceIDsLoading } = useQuery(
    () => (props.personID ? HistoryAPI.subscribeUserRelevant(props.personID)() : of([])),
    [props.personID],
  );

  // Which mandate or contract a version belongs to — the history only knows document ids. Fields the
  // viewer may not see (contracts without VIEW_CONTRACTS) arrive empty, and their versions simply
  // show no reference.
  const { value: referenced } = useQuery(
    () =>
      props.personID
        ? subscribeByID(
            Person,
            props.personID,
          )({
            _id: 1,
            sepaMandats: { _id: 1, mandateReference: 1 },
            contracts: {
              _id: 1,
              createdAt: 1,
              agInfos: { einstellungsdatum: 1, unterzeichnungsdatum: 1 },
              supplements: { _id: 1 },
            },
          })
        : of(null),
    [props.personID],
  );

  const references = new Map<string, string>();
  for (const mandate of referenced?.sepaMandats ?? [])
    if (mandate.mandateReference) references.set(mandate._id, `Mandatsreferenz ${mandate.mandateReference}`);
  for (const contract of referenced?.contracts ?? []) {
    const name = getReadableNameForContract(contract);
    references.set(contract._id, name);
    // a supplement has no name of its own, it is known by its contract
    for (const supplement of contract.supplements ?? []) references.set(supplement._id, `Nachtrag zu: ${name}`);
  }

  return HistoryTimeline({
    documentIDs: referenceIDs,
    loading: referenceIDsLoading,
    resolveReference: event => references.get(event.documentID ?? ''),
  });
});
